import { createBrowserRouter, RouterProvider, Navigate, useLocation } from "react-router-dom";
import PMLayout from "./components/layout/PMLayout";
import RequireAuth from "./components/routing/RequireAuth";

// ── your existing pages ──────────────────────────────────────────
import Dashboard        from "./pages/Dashboard";        // or PMDashboard.js
import PMDashboard      from "./pages/PMDashboard";
import MyTasks          from "./pages/MyTasks";
import ChatSpace        from "./pages/ChatSpace";
import EmailMessages    from "./pages/EmailMessages";
import AccessControl    from "./pages/AccessControl";
import Operations       from "./pages/Operations";
import OperationDetailsPage from "./pages/OperationDetailsPage";
import Incidents        from "./pages/Incidents";
import IncidentFormPage from "./pages/IncidentFormPage";
import Tickets          from "./pages/Tickets";
import TicketFormPage   from "./pages/TicketFormPage";
import TroubleshootingGuides from "./pages/TroubleshootingGuides";
import TroubleshootingGuideFormPage from "./pages/TroubleshootingGuideFormPage";
import Docs             from "./pages/Docs";
import Tags             from "./pages/Tags";
import Settings         from "./pages/Settings";
import Feedback         from "./pages/Feedback";
import Help             from "./pages/Help";
import Support          from "./pages/Support";
import Partners         from "./pages/Partners";
import PartnerCommissions from "./pages/PartnerCommissions";
import Users            from "./pages/Users";
import UserFormPage     from "./pages/UserFormPage";
import Profile          from "./pages/Profile";
import WorkspacesPage from "./workspaces/WorkspacesPage";
import WorkspaceCreatePage from "./workspaces/WorkspaceCreatePage";
import WorkspaceDetailsPage from "./workspaces/WorkspaceDetailsPage";
import WorkspaceSubscriptionsPage from "./workspaces/WorkspaceSubscriptionsPage";
import WorkspacePaymentMethodsPage from "./workspaces/WorkspacePaymentMethodsPage";
import WorkspaceOrganizationPage from "./workspaces/WorkspaceOrganizationPage";
import WorkspaceChargesPage from "./workspaces/WorkspaceChargesPage";
import WorkspaceMemberDetailsPage from "./workspaces/WorkspaceMemberDetailsPage";
import WorkspaceInvitationAcceptPage from "./workspaces/WorkspaceInvitationAcceptPage";

// project hierarchy pages — already in your pages/ folder
import Projects         from "./pages/Projects";              // list of all projects
import ProjectFormPage  from "./pages/ProjectFormPage";
import ProjectDetails   from "./pages/ProjectDetails";        // iterations inside a project
import IterationDetails from "./pages/IterationDetails";      // tasks inside an iteration
import Iterations       from "./pages/Iterations";            // (keep if you use it standalone)
import TaskDetailPage   from "./pages/TaskDetailPage";
import TaskFormPage     from "./pages/TaskFormPage";
import AuthLogin        from "./pages/AuthLogin";
import AuthSignup       from "./pages/AuthSignup";
import ProductOnboardingPage from "./pages/ProductOnboardingPage";
import AuthAppleSetup   from "./pages/AuthAppleSetup";
import AuthLinkedInCallback from "./pages/AuthLinkedInCallback";
import AuthRecoverRequest from './pages/AuthRecoverRequest';
import AuthRecoverVerify from './pages/AuthRecoverVerify';
import AuthRecoverReset from './pages/AuthRecoverReset';
import AuthRecoverSuccess from './pages/AuthRecoverSuccess';
import SafeLinkRedirect from "./pages/SafeLinkRedirect";
import PublicProductPage from "./pages/PublicProductPage";

// QA Testable Apps pages
import QaTestAppsPage from "./projects/pages/qa-tests";
import QaTestFeaturesPage from "./projects/pages/qa-tests/FeaturesPage";
import QaTestAppHistoryPage from "./projects/pages/qa-tests/AppHistoryPage";
import QaTestFeatureHistoryPage from "./projects/pages/qa-tests/FeatureHistoryPage";
// ─────────────────────────────────────────────────────────────────

const WorkspacesRedirect = () => {
  const { pathname, search, hash } = useLocation();
  return (
    <Navigate
      to={`${pathname.replace(/^\/product-accounts/, "/workspaces")}${search}${hash}`}
      replace
    />
  );
};

const WorkspaceOrganizationRedirect = () => {
  const { pathname, search, hash } = useLocation();
  return (
    <Navigate
      to={`${pathname.replace(/\/business-entity$/, "/organization")}${search}${hash}`}
      replace
    />
  );
};

const router = createBrowserRouter([
  { path: "/safe-link", element: <SafeLinkRedirect /> },
  { path: "/", element: <PublicProductPage /> },
  { path: "/about", element: <PublicProductPage /> },
  { path: "/contact-us", element: <PublicProductPage /> },
  { path: "/contact", element: <Navigate to="/contact-us" replace /> },
  { path: "/privacy-policy", element: <PublicProductPage /> },
  { path: "/privacy", element: <Navigate to="/privacy-policy" replace /> },
  { path: "/help", element: <PublicProductPage /> },
  { path: "/product", element: <Navigate to="/" replace /> },
  { path: "/product/about", element: <Navigate to="/about" replace /> },
  { path: "/product/contact", element: <Navigate to="/contact-us" replace /> },
  { path: "/product/privacy", element: <Navigate to="/privacy-policy" replace /> },
  { path: "/product/help", element: <Navigate to="/help" replace /> },
  { path: "/login", element: <AuthLogin /> },
  { path: "/get-started", element: <ProductOnboardingPage /> },
  { path: "/auth/landing", element: <Navigate to="/login" replace /> },
  { path: "/auth/login", element: <Navigate to="/login" replace /> },
  { path: "/auth/signup", element: <AuthSignup /> },
  { path: "/auth/apple-setup", element: <AuthAppleSetup /> },
  { path: "/auth/social-setup", element: <AuthAppleSetup /> },
  { path: "/auth/linkedin/callback", element: <AuthLinkedInCallback /> },
  { path: "/auth/forgot-password", element: <Navigate to="/auth/recover" replace /> },
  { path: "/auth/recover", element: <AuthRecoverRequest /> },
  { path: "/auth/verify", element: <AuthRecoverVerify /> },
  { path: "/auth/reset", element: <AuthRecoverReset /> },
  { path: "/auth/recovered", element: <AuthRecoverSuccess /> },
  { path: "/auth/session-expired", element: <AuthLogin /> },
  { path: "/auth/linkedin/callback", element: <AuthLinkedInCallback /> },
  { path: "/auth/logout", element: <Navigate to="/auth/login" replace /> },
  {
    path: "/",
    element: (
      <RequireAuth>
        <PMLayout />
      </RequireAuth>
    ),
    children: [

      // overview
      { path: "home",      element: <Dashboard />  },
      { path: "dashboard", element: <PMDashboard /> },
      { path: "chat-space", element: <ChatSpace /> },
      { path: "email-messages", element: <EmailMessages /> },
      { path: "access-control", element: <AccessControl /> },
      { path: "operations", element: <Operations showTabs /> },
      { path: "incidents", element: <Incidents /> },
      { path: "incidents/new", element: <IncidentFormPage /> },
      { path: "incidents/:incidentId/edit", element: <IncidentFormPage /> },
      { path: "incidents/:incidentId", element: <OperationDetailsPage resource="incidents" /> },
      { path: "tickets", element: <Tickets /> },
      { path: "tickets/new", element: <TicketFormPage /> },
      { path: "tickets/:ticketId/edit", element: <TicketFormPage /> },
      { path: "tickets/:ticketId", element: <OperationDetailsPage resource="tickets" /> },
      { path: "troubleshooting-guides", element: <TroubleshootingGuides /> },
      { path: "troubleshooting-guides/new", element: <TroubleshootingGuideFormPage /> },
      { path: "troubleshooting-guides/:guideId/edit", element: <TroubleshootingGuideFormPage /> },
      { path: "troubleshooting-guides/:guideId", element: <OperationDetailsPage resource="guides" /> },
      { path: "docs", element: <Docs /> },
      { path: "docs/:docId", element: <Docs /> },
      { path: "my-tasks",  element: <MyTasks />    },

      // ── projects drill-down hierarchy ──────────────────────────
      { path: "projects",                                                    element: <Projects />         },
      { path: "projects/new",                                               element: <ProjectFormPage />  },
      { path: "projects/:projectId",                                         element: <ProjectDetails />   },
      { path: "projects/:projectId/edit",                                    element: <ProjectFormPage />  },
      { path: "projects/:projectId/iterations",                              element: <Iterations />       },
      { path: "projects/:projectId/iterations/new",                          element: <ProjectDetails />   },
      { path: "projects/:projectId/iterations/:iterationId/edit",            element: <ProjectDetails />   },
      { path: "projects/:projectId/iterations/:iterationId",                 element: <IterationDetails /> },
      { path: "projects/:projectId/iterations/:iterationId/tasks/:taskId",   element: <TaskDetailPage />   },
      { path: "projects/:projectId/iterations/:iterationId/tasks/new",      element: <TaskFormPage />     },
      { path: "projects/:projectId/iterations/:iterationId/tasks/:taskId/edit", element: <TaskFormPage />  },
      // ──────────────────────────────────────────────────────────

      // manage
      { path: "users",     element: <Users />      },
      { path: "users/new",  element: <UserFormPage /> },
      { path: "users/:userId/edit", element: <UserFormPage /> },
      { path: "users/:userId", element: <Profile /> },
      { path: "workspaces", element: <WorkspacesPage /> },
      { path: "workspaces/new", element: <WorkspaceCreatePage /> },
      { path: "workspaces/invitations/accept", element: <WorkspaceInvitationAcceptPage /> },
      { path: "workspaces/:workspaceId/invitations/:invitationId", element: <WorkspaceInvitationAcceptPage /> },
      { path: "workspaces/:workspaceId", element: <WorkspaceDetailsPage /> },
      { path: "workspaces/:workspaceId/members/:userId", element: <WorkspaceMemberDetailsPage /> },
      { path: "workspaces/:workspaceId/subscriptions", element: <WorkspaceSubscriptionsPage /> },
      { path: "workspaces/:workspaceId/payment-methods", element: <WorkspacePaymentMethodsPage /> },
      { path: "workspaces/:workspaceId/organization", element: <WorkspaceOrganizationPage /> },
      { path: "workspaces/:workspaceId/business-entity", element: <WorkspaceOrganizationRedirect /> },
      { path: "workspaces/:workspaceId/charges", element: <WorkspaceChargesPage /> },
      { path: "product-accounts", element: <WorkspacesRedirect /> },
      { path: "product-accounts/*", element: <WorkspacesRedirect /> },
      { path: "partners",  element: <Partners />   },
      { path: "partners/commissions", element: <PartnerCommissions /> },
      { path: "tags",      element: <Tags />       },
      { path: "settings",  element: <Settings />   },
      { path: "feedback",  element: <Feedback />   },
      { path: "app/help",  element: <Help />       },
      { path: "support",   element: <Support />    },
      { path: "profile",   element: <Profile />    },

      // qa-test-runs
      { path: "qa-test-runs", element: <QaTestAppsPage /> },
      { path: "qa-test-runs/apps/:appId/features", element: <QaTestFeaturesPage /> },
      { path: "qa-test-runs/apps/:appId/history", element: <QaTestAppHistoryPage /> },
      { path: "qa-test-runs/apps/:appId/features/:featureId/history", element: <QaTestFeatureHistoryPage /> },

      // fallback
      { path: "*",         element: <Navigate to="/home" replace /> },
    ],
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
